// Sentry initialization for the frontend (Vite + React)
// This module safely initializes Sentry only when a DSN is provided.
// It uses dynamic imports so the project still builds even if @sentry/* packages
// are not yet installed. To enable, set VITE_SENTRY_DSN_FRONTEND and add deps.

// Recommended env vars (set via .env, Dockerfile, or CI):
// - VITE_SENTRY_DSN_FRONTEND
// - VITE_SENTRY_ENV (e.g., production, staging, development)
// - VITE_SENTRY_RELEASE (CI-provided commit SHA or version)
// - VITE_SENTRY_TRACES_SAMPLE_RATE (e.g., 0.1 in prod)
// - VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE (e.g., 0.01 in prod)
// - VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE (e.g., 1.0)

import { getAppConfigValue } from "./utils/appConfig"

const DSN = getAppConfigValue("VITE_SENTRY_DSN_FRONTEND") || undefined
const ENV = getAppConfigValue("VITE_SENTRY_ENV") || import.meta.env.MODE || 'development'
const ENABLED = String(getAppConfigValue("VITE_SENTRY_ENABLED") || (ENV === 'production' ? 'true' : 'false')).toLowerCase() === 'true'

if (ENABLED && DSN && typeof window !== 'undefined') {
  const RELEASE = getAppConfigValue("VITE_SENTRY_RELEASE") || ''

  const isProd = ENV === 'production'
  const tracesSampleRate = parseFloat(String(getAppConfigValue("VITE_SENTRY_TRACES_SAMPLE_RATE") || (isProd ? 0.1 : 1)))
  const replaysSessionSampleRate = parseFloat(String(getAppConfigValue("VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE") || (isProd ? 0.01 : 0)))
  const replaysOnErrorSampleRate = parseFloat(String(getAppConfigValue("VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE") || 1))

  // Dynamic imports keep build green if deps are missing until you add them.
  Promise.all([
    import('@sentry/react'),
  ])
    .then(async ([Sentry]) => {
      // Optional Replay: only use if available in the same package to avoid version mismatch
      let replayIntegration: any | undefined
      try {
        if ((replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0) && typeof (Sentry as any).replayIntegration === 'function') {
          replayIntegration = (Sentry as any).replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          })
        }
      } catch (e) {
        // Ignore if replay integration is not available
      }

      // Browser tracing
      let browserTracingIntegration: any | undefined
      try {
        // For general SPA tracing; TanStack Router will still be captured via history/Fetch/XHR
        browserTracingIntegration = (Sentry as any).browserTracingIntegration?.()
      } catch (e) {
        // older/newer API mismatch; ignore
      }

      const integrations = [
        browserTracingIntegration,
        replayIntegration,
      ].filter(Boolean)

      // Build options and only set Replay sample rates when Replay is present
      const options: any = {
        dsn: DSN,
        environment: ENV,
        release: RELEASE || undefined,
        integrations,
        tracesSampleRate,
        // Scrub PII-like values from breadcrumbs/events
        beforeSend(event: any) {
          // Remove potentially sensitive headers/query params if present
          if (event.request) {
            // Scrub cookies and Authorization
            if (event.request.headers) {
              const h = { ...event.request.headers }
              delete (h as any)['cookie']
              delete (h as any)['Cookie']
              delete (h as any)['authorization']
              delete (h as any)['Authorization']
              event.request.headers = h
            }

            // Optionally drop query strings from URL for privacy
            if (event.request.url) {
              try {
                const u = new URL(event.request.url)
                u.search = ''
                event.request.url = u.toString()
              } catch {}
            }
          }
          return event
        },
        // Limit events to this app's origin
        allowUrls: [
          // current origin
          typeof location !== 'undefined' ? location.origin : undefined,
        ].filter(Boolean) as string[],
        // Reduce noise in development
        debug: !isProd,
      }

      if (replayIntegration) {
        options.replaysSessionSampleRate = replaysSessionSampleRate
        options.replaysOnErrorSampleRate = replaysOnErrorSampleRate
      }

      ;(Sentry as any).init(options)
    })
    .catch(() => {
      // Sentry packages not installed or failed to init; ignore to avoid breaking app
    })
}
